<template>
  <HeaderBar
    class="hide-for-medium"
    :modifiers="['dark', 'sticky']"
  >
    <router-link
      :aria-label="$t('general.back')"
      class="header-bar__title typo-nav-item"
      :to="{ name: inspectionRoute.name, params: { scope: 'inspections' } }"
    >
      <LeftArrowIcon class="icon" />

      <div class="typo-small-title">
        {{ $t('nav.inspections') }}
      </div>
    </router-link>
  </HeaderBar>

  <InspectionNav
    v-if="currentOperation"
    :conditionLevel="conditionLevel.toString()"
    :currentOperation="currentOperation"
    :filterByOptions="filterByOptions"
    :inspection="inspection"
    :issues="issues"
    :loading="loading"
    :newIssueRoute="shaftData"
    :riskLevel="riskLevel.toString()"
    :shaftStatus="{ isShaft, isShaftV2 }"
    @delete-inspection="deleteInspection"
    @filter-by="updateFilter"
    @update-inspection="saveInspection"
    @update-status="updateStatus"
  />

  <!-- Issues List  -->
  <div
    v-if="!loading"
    class="scroll-container"
  >
    <div class="issue-list">
      <ul class="issue-list__issues">
        <IssueCard
          v-for="issue in issues"
          :key="issue.id"
          :issue="issue"
          :modifiers="['responsive']"
          :tags="true"
        />
      </ul>
    </div>
    <EmptyMessage
      v-if="inspection?.issues?.length === 0"
      :message="$t(`empty_states.issues.no_issues.${scope}`)"
      :title="$t('empty_states.issues.title')"
    >
      <div class="empty-message__image">
        <CircleSquareTarget />
      </div>
    </EmptyMessage>
  </div>
  <Loader v-if="loading" />
</template>

<script setup>
import { watch, ref, onMounted, computed, reactive } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import I18n from 'i18n-js';

// Utils
import calculateConditionRating from '@/functions/simm/shafts/condition_rating';
import getRiskRatingLevel from '@/functions/simm/shafts/get_risk_rating_level';
import useAjax from '@/functions/use_ajax';
import useEmitter from '@/functions/use_emitter';
import { useShaft, useShaftV2 } from '@/functions/use_remote_object';

// Stores
import { useIssueLocationStore } from '@/stores/shafts/issueLocationStore';

// Components
import EmptyMessage from '@/components/generic/empty_message.vue';
import HeaderBar from '@/components/generic/header_bar.vue';
import InspectionNav from '@/components/generic/inspection_nav.vue';
import IssueCard from '@/components/issues/issue_card.vue';
import Loader from '@/components/generic/loader.vue';

// Icons
import CircleSquareTarget from '@/images/circle-square-target.svg';
import LeftArrowIcon from '@/images/arrow-left.svg';

const emit = defineEmits(['statusChange', 'updateInspection', 'deleteInspection']);
const props = defineProps({
  organisationId: { type: String, required: true },
  inspectionId: { type: String, required: true },
  shaftId: { type: String, required: true },
  scope: { type: String, default: 'inspections' },
});

const shaftIssueLocationStore = useIssueLocationStore();

const ajax = useAjax();
const router = useRouter();
const route = useRoute();
const emitter = useEmitter();

const isShaft = computed(() => route.path.includes('shafts'));
const isShaftV2 = computed(() => route.path.includes('shafts_v2'));
const currentOperation = isShaftV2.value ? useShaftV2(props) : useShaft(props);

const shaftData = computed(() => {
  if (isShaftV2.value) {
    return { name: 'newShaftV2Issue', url: 'shafts_v2/shafts' };
  } else {
    return { name: 'newShaftIssue', url: 'shafts' };
  }
});

const inspection = reactive({
  approved_by: null,
  date: null,
  inspected_by: null,
  inspection_type: null,
  policy: {},
  status: null,
  risk_rating: 0,
  condition_rating: 0,
  issues: [],
  trusted_user: false
});
const issues = ref([]);
const loading = ref(true);
const filterBy = ref(null);
const filterByOptions = ref([]);

const riskLevel = ref(0);
const conditionLevel = ref(0);

onMounted(() => {
  fetchInspection();
  shaftIssueLocationStore.$reset();
});

/**
 * Watches for changes on (:inspectionId) route param then,
 *  fetches the inspection by id
 */
watch(() => props.inspectionId, fetchInspection);

/**
 * Watches for changes on the filter by dropdown
 */
watch(filterBy, () => {
  loading.value = true;
  if (!filterBy.value) {
    issues.value = inspection.issues;
  } else {
    issues.value = inspection.issues.filter((item) => item.status === filterBy.value);
  }
  loading.value = false;
});

const inspectionRoute = computed(() => {
  return {
    name: isShaftV2.value ? 'shaftV2Inspections' : 'shaftInspections',
    url: isShaftV2.value ? 'shafts_v2/shafts' : 'shafts'
  };
});

const ENDPOINTS = computed(() => {
  return {
    inspection: `/organisations/${props.organisationId}/${inspectionRoute.value.url}/${props.shaftId}/${props.scope}/${props.inspectionId}`,
    inspectionApproval: `/organisations/${props.organisationId}/${inspectionRoute.value.url}/${props.shaftId}/inspections/${props.inspectionId}`
  };
});

/**
 * Fetches the inspection details from the server
 */
async function fetchInspection() {
  if (props.inspectionId) {
    loading.value = true;
    const { ok, data } = await ajax(ENDPOINTS.value.inspection);
    if (ok) {
      Object.assign(inspection, data);
      riskLevel.value = getRiskRatingLevel(data?.risk_rating);
      conditionLevel.value = calculateConditionRating(data?.condition_rating);
      issues.value = [...inspection.issues];
      filterByOptions.value = generateFilterList(issues.value, 'status');
    }
    loading.value = false;
  }
}

/**
 * Generates a list of all unique statuses
 * available in the current inspection's issue list
 */
function generateFilterList(data, key) {
  if (!data) return;

  /**
   * Creates a list of all unique key items (e.g status)
   * that exist in the provided data set
   */
  const mapData = data.reduce((p,c) => p.has(c[key]) ? p.set(c[key]) : p.set(c[key]), new Map());
  /**
   * Maps the unique list and formats a new
   * array with other relevant data
   */
  const result = [...mapData].map(e => {
    const count = data.filter(obj => obj[key] === e[0]).length;
    const status = `${e[0] !== 'resolved' ? e[0] : e[0] + '_issue'}`;
    const labelText = count <= 1 ? I18n.t(`inspections.issues.filter.${status}`) : I18n.t(`inspections.issues.filter.${status}s`);
    return e[0] && ({value: e[0], label: labelText, total: count});
  });
  result[0] && result.unshift({value: null, label: I18n.t('inspections.issues.filter.all_issues'), total: data.length});
  return result[0] && result || [];
}

/**
 * Updates the inspection status
 *  and emits 'statusChange' to parent to handle update
 */
async function updateStatus(newStatus) {
  inspection.status = newStatus;

  let { ok, data } = await ajax(`${ENDPOINTS.value.inspectionApproval}/approval`, {
    method: 'PATCH',
    body: { inspection: { status: newStatus } },
  });

  if (ok) {
    Object.assign(inspection, data);
    emit('statusChange', data);
  }
}

/**
 * Save inspection
 */
async function saveInspection(data) {
  Object.assign(inspection, data);
  emit('updateInspection', data);
}

async function deleteInspection() {
  let { ok } = await ajax(
    ENDPOINTS.value.inspection,
    { method: 'DELETE' }
  );

  if (ok) {
    emit('deleteInspection');
    router.replace({ name: inspectionRoute.value.name, params: { scope: 'inspections' } });
    emitter.emit('toast', {
      status: 'success',
      message: I18n.t('inspections.inspection_delete_toast_success'),
    });
  } else {
    emitter.emit('toast', {
      status: 'error',
      message: I18n.t('inspections.inspection_delete_toast_error'),
    });
  }
}

function updateFilter(value) {
  filterBy.value = value;
}
</script>
